<!--
    HOC (Higher-Order Component) that dispatches to the correct tile layout
    based on the `tileLayout` prop. Add new layout variants here.

    Experiment routing:
    - CLEAN_TILES (E3801B): swap to the E3801B layout variants. The experiment
      is orthogonal to the BracketLayout (which controls aspect ratio / columns),
      so the decision lives here rather than in BracketLayout.applyExperimentOverride.
-->
<script lang="ts">
    import type { Variation, VariationV2 } from '@shared/types/types.ts';
    import type { ImageAspectRatio } from '@tile/components/product-image/types.ts';
    import { injectWiring } from '@tilelist/application/wiring/TileListWiringContext.svelte.ts';
    import type { TileLayout } from '@tilelist/domain/composition/BracketLayout.ts';
    import DefaultLayoutE3801B from '@tilelist/layouts/__experiments__/e3801b/default-layout/DefaultLayout.svelte';
    import FashionLayoutE3801B from '@tilelist/layouts/__experiments__/e3801b/fashion-layout/FashionLayout.svelte';
    import DefaultLayout from '@tilelist/layouts/default-layout/DefaultLayout.svelte';
    import { createDefaultLayout } from '@tilelist/layouts/default-layout/DefaultLayout.ts';
    import FashionLayout from '@tilelist/layouts/fashion-layout/FashionLayout.svelte';
    import { createFashionLayout } from '@tilelist/layouts/fashion-layout/FashionLayout.ts';
    import type { TileLayoutProps } from '@tilelist/layouts/shared/TileLayoutProps.ts';

    type Props = TileLayoutProps & {
        tileLayout: TileLayout;
        variation: Variation | VariationV2;
        aspectRatio: ImageAspectRatio;
        showTopInfos: boolean;
        showInstallments: boolean;
    };

    let { tileLayout, variation, aspectRatio, showTopInfos, showInstallments, ...coreProps }: Props = $props();

    const isCleanTiles = $derived(injectWiring().onex.isExperimentGroup('CLEAN_TILES', 'E3801B'));
</script>

{#if tileLayout === 'fashion' && isCleanTiles}
    <FashionLayoutE3801B {...coreProps} layout={createFashionLayout(variation)} {aspectRatio} />
{:else if tileLayout === 'fashion'}
    <FashionLayout {...coreProps} layout={createFashionLayout(variation)} {aspectRatio} />
{:else if isCleanTiles}
    <DefaultLayoutE3801B
        {...coreProps}
        layout={createDefaultLayout(variation, showTopInfos)}
        {aspectRatio}
        {showInstallments}
    />
{:else}
    <DefaultLayout
        {...coreProps}
        layout={createDefaultLayout(variation, showTopInfos)}
        {aspectRatio}
        {showInstallments}
    />
{/if}
